import { Dispatch, SetStateAction, startTransition, useCallback, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { ETypographyColor, ETypographySize, ETypographyWeight, Typography } from "@/3514/components"
import { buildCssClassName } from "@/3514/utils"
import { CalendarAccount } from "@/entities/coach-calendar"
import Accordion from "@/shared/accordion/Accordion"
import Card from "@/shared/card/Card"
import ChevronToggle from "@/shared/chevron-toggle/ChevronToggle"

import { CalendarPrimaryItem } from "./CalenadarPrimaryItem"

import "./styles.scss"

type Props = {
    data: CalendarAccount[]
    isRedesigned?: boolean
}

export const CalendarPrimary = ({ data, isRedesigned = false }: Props) => {
    const [active, setActive] = useState(true)

    const [accordionTitle, setAccordionTitle]: [string, Dispatch<SetStateAction<string>>] = useState<string>(String())

    const { t } = useTranslation()

    const primaryProfile = useMemo(() => data?.find(calendar => calendar.coachHasPrimaryCalendarProfile), [data])

    const primaryCalendar = useMemo(
        () => primaryProfile?.profileCalendars?.find(calendar => calendar.isCalendarPrimary),
        [primaryProfile]
    )

    const handleClick = useCallback(
        (title: string) =>
            startTransition((): void => {
                setActive(false)
                setAccordionTitle(title)
            }),
        []
    )

    return (
        <Card className="calendar-primary text-left" isRedesigned={isRedesigned}>
            <div
                className={buildCssClassName("calendar-primary__header cursor-pointer", {
                    "!py-[8px] !px-[15px] flex items-center": isRedesigned
                })}
                onClick={() => startTransition((): void => setActive(!active))}
            >
                <div
                    className={buildCssClassName("flex-1", {
                        "flex items-center gap-x-[10px] line-clamp-1": isRedesigned
                    })}
                >
                    <Typography
                        text={
                            primaryProfile
                                ? `${primaryProfile?.profileName}: `
                                : accordionTitle || t("coachSide.linkCalendar.selectCalendarBlock.formTitle")
                        }
                        color={isRedesigned ? ETypographyColor.Black : ETypographyColor.Error}
                        className={buildCssClassName({ "max-w-[50%] truncate": isRedesigned })}
                        weight={isRedesigned ? ETypographyWeight.Default : ETypographyWeight.Bold}
                        size={isRedesigned ? ETypographySize.Medium : ETypographySize.Small}
                    />

                    <span className={buildCssClassName({ "max-w-[50%] truncate": isRedesigned })}>
                        {primaryCalendar?.calendarName}
                    </span>
                </div>

                <ChevronToggle active={active} {...(isRedesigned && { variant: "actual" })} />
            </div>
            <Accordion active={active}>
                {data?.map(calendar => (
                    <CalendarPrimaryItem onClick={handleClick} calendar={calendar} isRedesigned={isRedesigned} />
                ))}
            </Accordion>
        </Card>
    )
}
